export class Ticker {
  private handle: number | undefined;

  public constructor(private readonly cb: () => void) {
    this.start();
  }

  public start() {
    const loop = () => {
      this.handle = requestAnimationFrame(loop);
      this.cb();
    };
    loop();
  }

  public stop() {
    if (this.handle) cancelAnimationFrame(this.handle);
    this.handle = undefined;
  }
}

export function generateUid(): string {
  return crypto.randomUUID();
}

export function random(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

export function sampleRandom<T>(arr: T[], max: number): T[] {
  if (arr.length <= max) return arr.slice();

  return shuffle(arr).slice(0, max);
}

export function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => 0.5 - Math.random());
}

export function excludeFrom<T>(arr: T[], ...except: T[]): T[] {
  return arr.filter((item) => !except.includes(item));
}

export function notNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function getUntransformedBoundingBox(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const transform =
    `${style.transform === 'none' ? '' : style.transform} ${style.translate.length > 0 && style.translate !== 'none' ? `translate(${style.translate.split(' ').join(',')})` : ''} `.trim();

  if (transform.length > 0) {
    const matrix = new DOMMatrix(transform);

    const scaleX = matrix.a;
    const scaleY = matrix.d;
    const translateX = matrix.e;
    const translateY = matrix.f;

    return {
      x: rect.x - translateX,
      y: rect.y - translateY,
      width: rect.width / scaleX,
      height: rect.height / scaleY,
    };
  }

  return rect;
}

export type DiffOp<T> =
  | { type: 'insert'; index: number; value: T }
  | { type: 'remove'; index: number; value: T }
  | { type: 'replace'; index: number; oldValue: T; newValue: T }
  | { type: 'equal'; index: number; value: T };

export function calcArrayDiff<T>(oldList: T[], newList: T[]): DiffOp<T>[] {
  const m = oldList.length;
  const n = newList.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  // Fill base cases
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  // Fill DP table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldList[i - 1] === newList[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1, // remove
          dp[i][j - 1] + 1, // insert
          dp[i - 1][j - 1] + 1, // replace
        );
      }
    }
  }

  // Backtrack to build list of operations
  const ops: DiffOp<T>[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldList[i - 1] === newList[j - 1]) {
      ops.unshift({ type: 'equal', index: i - 1, value: oldList[i - 1] });
      i--;
      j--;
    } else if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
      ops.unshift({
        type: 'replace',
        index: i - 1,
        oldValue: oldList[i - 1],
        newValue: newList[j - 1],
      });
      i--;
      j--;
    } else if (j > 0 && dp[i][j] === dp[i][j - 1] + 1) {
      ops.unshift({ type: 'insert', index: i, value: newList[j - 1] });
      j--;
    } else if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      ops.unshift({ type: 'remove', index: i - 1, value: oldList[i - 1] });
      i--;
    } else {
      break; // should not happen
    }
  }

  return ops;
}
